import type { ReactNode } from 'react';
import { Switch } from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  MODE_INSTANCES,
  type DatabaseMode,
  type FieldValues,
} from '@internal/plugin-waadoo-config-common';
import { waadooIcons } from '../../ui/icons/waadooIcons';
import { SectionCard } from '../../waadooForm';
import { projectEnvironmentsTranslationRef } from '../translation';
import { ConfigurationRow } from './ConfigurationRow';
import { DATABASE_MODE_KEYS, type DatabaseFields } from './databaseFields';
import type {
  ConfigurationFieldOf,
  ConfigurationRows,
} from './useConfigurationRows';

/**
 * The PostgreSQL section of the « Configuration » tab (genericity plan,
 * step 4), the same for the Odoo tab and the generic one: the instances
 * (one, two or three, `databaseMode`), the synchronous replication, the CPU
 * and the memory of each instance, the size of its volume — then `children`,
 * the caller's rows of the same card (Odoo: its filestore).
 *
 * `fields`: the CloudNativePG component's fields by role
 * (`databaseFieldsOf`); `modeOf` reads the mode of a configuration — the
 * draft, the reference —; `synchronous`, the replication as planned (the
 * engine turns it off with a single instance).
 */
export function DatabaseSection<C extends FieldValues>(props: {
  title: string;
  titleId: string;
  rows: ConfigurationRows<C>;
  fields: DatabaseFields<ConfigurationFieldOf<C>>;
  modeOf: (config: C) => DatabaseMode;
  synchronous: boolean;
  children?: ReactNode;
}) {
  const { rows, fields, modeOf } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const mode = modeOf(rows.draft);

  return (
    <SectionCard
      icon={waadooIcons.database}
      title={props.title}
      titleId={props.titleId}
    >
      <ConfigurationRow
        label={t('configuration.instances')}
        labelId={rows.ids(fields.mode)}
        lockedLabel={rows.lockedLabel}
        hint={rows.hintText(rows.was(fields.mode), t(DATABASE_MODE_KEYS[mode]))}
        reference={rows.referenceText(config =>
          String(MODE_INSTANCES[modeOf(config)]),
        )}
      >
        {rows.choice<DatabaseMode>({
          label: rows.label(fields.mode),
          selected: mode,
          items: [
            { id: 'single', label: t('configuration.instances1') },
            { id: 'ha2', label: t('configuration.instances2') },
            { id: 'ha3zones', label: t('configuration.instances3') },
          ],
          disabled: rows.isDisabled(fields.mode),
          onSelect: id => rows.set(fields.mode, id),
        })}
      </ConfigurationRow>
      <ConfigurationRow
        label={rows.label(fields.sync)}
        labelId={rows.ids(fields.sync)}
        lockedLabel={rows.lockedLabel}
        hint={
          mode === 'single'
            ? rows.hintText(t('configuration.syncSingle'))
            : undefined
        }
        reference={rows.referenceText(rows.fieldsText(fields.sync))}
      >
        <Switch
          aria-labelledby={rows.ids(fields.sync)}
          isSelected={props.synchronous}
          isDisabled={rows.isDisabled(fields.sync) || mode === 'single'}
          onChange={value => rows.set(fields.sync, value)}
        />
      </ConfigurationRow>
      {rows.pairRow(
        'dbCpu',
        t('configuration.dbCpu'),
        fields.cpuRequest,
        fields.cpuLimit,
        t('configuration.perInstance'),
      )}
      {rows.pairRow(
        'dbMemory',
        t('configuration.dbMemory'),
        fields.memoryRequest,
        fields.memoryLimit,
        t('configuration.perInstance'),
      )}
      {rows.sizeRow(fields.size, t('configuration.growOnly'))}
      {props.children}
    </SectionCard>
  );
}
